// DS: TA0.4
import React from 'react';
import classnames from 'classnames';
import { PapiDaSharedUserInfo } from '@wix/da-papi-types';
import { Url } from '@wix/da-url';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import { useTranslation } from 'react-i18next';
import UserAvatar from '../Avatar/UserAvatar';
import UserLink from '../Link/UserLink';
import DeviationDumbView from '../../DeviationViews/Dumbo/MinimalDumbo';
import ProfileOwnerWatchButton from '../../Button/ProfileOwnerWatchButton';
import DeviationLink from '../../Duperbrowse/DeviationLink';
import { getInfoToDisplayImageDeviation } from '../../DeviationViews/Dumbo/Image/_helpers';
import shortenNumber from '@wix/da-ds/pkg/utils/shortenNumber';
import { useDeviationMatureFilter } from '../../utils/hooks/useDeviationMatureFilter';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import { useVerification } from '../../VerificationPopup/withVerification';
import MonetizationPromoStrip from './MonetizationPromoStrip';
import {
  BiData,
  BiContextProvidedValue,
  MessageIconClickBiEvent,
  SeeAllClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';

import s from './UserAvatarPopoverContent.scss';

export interface UserAvatarPopoverContentProps {
  withButtons: boolean;
  relatedItemBiData?: any;
}

const getBackgroundUrl = (deviation): string | undefined => {
  const info = getInfoToDisplayImageDeviation({
    deviation,
    width: parseInt(s['background-picture-width']),
  });
  if (info) {
    return info.pickedImage.src;
  }
};

export const UserAvatarPopoverContent: React.FC<
  PapiDaSharedUserInfo & UserAvatarPopoverContentProps
> = ({
  user,
  showMonetizationPromo,
  deviationsCount = 0,
  featuredDeviations: deviations,
  withButtons,
  relatedItemBiData,
  isContactable,
}) => {
  const { showBlocked } = useDeviationMatureFilter(user?.profile?.profilePic);
  const { restrictToVerifiedAndLoggedIn } = useVerification();
  const { t } = useTranslation();
  if (!user) {
    return null;
  }
  const { username, profile, stats } = user;
  const watchersCount = (stats && stats.watchers) || 0;
  const deviationStyle: React.CSSProperties = {};
  const deviationClassNames: string[] = [];

  let backgroundImageSrc;
  if (profile?.profilePic) {
    backgroundImageSrc = getBackgroundUrl(profile.profilePic);
  }
  if (backgroundImageSrc) {
    deviationStyle.backgroundImage = `url('${backgroundImageSrc}')`;
  } else {
    deviationClassNames.push(s['no-image']);
    deviationClassNames.push(s['default-hero-background']);
  }

  return (
    <div className={classnames(s['root'], 'ds-surface-tertiary')}>
      <div className={classnames(s['content-with-background'])}>
        <div
          className={classnames(
            s['content-background'],
            showBlocked && s['mature-blur'],
            deviationClassNames
          )}
          style={deviationStyle}
        ></div>
        {showMonetizationPromo && (
          <MonetizationPromoStrip monetizationPromo={showMonetizationPromo} />
        )}
        <div className={classnames(s['content-info'])}>
          <div className={s['content-with-mask']}>
            <UserAvatar user={user} size={50} />
            <UserLink className={s['username']} user={user} />
            {profile && profile.tagline && <div className={s['tagline']}>{profile.tagline}</div>}
            <div className={s['stats']}>
              <div className={s['stat-item']}>
                {t('avatar.deviations', {
                  formattedCount: shortenNumber(deviationsCount),
                  count: deviationsCount,
                })}
              </div>
              <div className={s['stat-item']}>{' | '}</div>
              <div className={s['stat-item']}>
                {t('avatar.watchers', {
                  formattedCount: shortenNumber(watchersCount),
                  count: watchersCount,
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={s['featured-buttons-wrapper']}>
        <div className={s['deviations']}>
          {deviations &&
            deviations.length > 0 &&
            deviations.slice(0, 4).map(deviation => (
              <DeviationLink
                key={deviation.deviationId}
                deviation={deviation}
                title={deviation.title}
                className={s['deviation-item']}
              >
                <DeviationDumbView
                  width={parseInt(s['deviation-item-width'])}
                  height={parseInt(s['deviation-item-height'])}
                  deviation={deviation}
                />
              </DeviationLink>
            ))}
          <BiLink
            href={Url.userGalleryLink({
              username,
              gallectionType: 'gallery',
            })}
            className={s['see-all-link']}
            biData={BiData<SeeAllClickBiEvent>({
              evid: 106,
              typeid: undefined,
              itemid: undefined,
              creatorid: user.useridUuid,
            })}
          >
            {t('avatar.seeAll')}
          </BiLink>
        </div>
        {withButtons && (
          <div className={s['buttons-block']}>
            <GhostButton
              size="large"
              variant="main"
              className={classnames(s['button-message'], s['button-item'])}
              onClick={restrictToVerifiedAndLoggedIn(ReferralType.MESSAGES, 'messages', () => {
                window.location.assign(Url.userMessageLink(username));
              })}
              biData={BiData<MessageIconClickBiEvent>({
                evid: 470,
                recipientid: user.useridUuid,
                sectionname: BiContextProvidedValue,
              })}
            >
              {t('avatar.damz.message')}
            </GhostButton>
            <ProfileOwnerWatchButton
              user={user}
              initialData={{
                isWatching: !!user.isWatching,
                isLoading: false,
              }}
              className={s['button-item']}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default UserAvatarPopoverContent;
